import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { CoverSheet } from "./CoverSheet.js";
import { AutoField, SectionCard, useAction } from "./fields.js";
import { usePageJobs } from "./PageGeneration.js";
import { useProviderStatus } from "./providers.js";
import type { ColoringVariant } from "./types.js";

const COLOR_FIELDS: { key: "titleColor" | "subtitleColor" | "textColor" | "backgroundColor"; label: string }[] = [
  { key: "titleColor", label: "Title" },
  { key: "subtitleColor", label: "Subtitle" },
  { key: "textColor", label: "Back text" },
  { key: "backgroundColor", label: "Spine / plate" }
];

/**
 * The Cover tab. The AI makes the artwork only; the title, subtitle, author and
 * back-cover text are typeset by us so they are always crisp and spelled correctly.
 */
export function CoverPanel() {
  const { projectId, state, saveBook, reload } = useColoring();
  const toast = useToast();
  const { busy, run } = useAction();
  const providers = useProviderStatus();
  const { project, book } = state;
  const c = book.cover;
  const front = usePageJobs("cover-front");
  const back = usePageJobs("cover-back");
  const noProvider = providers !== null && !providers.active;
  const [variants, setVariants] = useState<ColoringVariant[]>([]);
  const frontFile = useRef<HTMLInputElement | null>(null);
  const backFile = useRef<HTMLInputElement | null>(null);

  const loadVariants = useCallback(async () => {
    try {
      setVariants((await coloringApi.coverVariants(projectId)).variants);
    } catch {
      /* history is a convenience */
    }
  }, [projectId]);
  useEffect(() => {
    void loadVariants();
  }, [loadVariants, c.frontVariantId, c.backVariantId]);

  const generate = (side: "front" | "back") =>
    run(`gen-${side}`, async () => {
      await coloringApi.generateCover(projectId, side);
      await reload();
    }, `Queued the ${side} cover.`);

  const upload = async (side: "front" | "back", file: File | undefined) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("That image is larger than 25 MB.");
      return;
    }
    await run(`up-${side}`, async () => {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error("Could not read that file."));
        r.readAsDataURL(file);
      });
      await coloringApi.uploadCover(projectId, side, dataUrl);
      await reload();
    }, `Your ${side} cover artwork was added.`);
  };

  const saveProject = async (patch: { title?: string; subtitle?: string | null }) => {
    try {
      await api.updateProject(projectId, patch);
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save.");
    }
  };

  const sideStrip = (side: "front" | "back") => {
    const list = variants.filter((v) => (v.settings.side === "back" ? "back" : "front") === side);
    if (list.length < 2) return null;
    const current = side === "front" ? c.frontVariantId : c.backVariantId;
    return (
      <div className="variant-strip" style={{ marginTop: 10 }}>
        {list.map((v) => (
          <button key={v.id} className={`variant-thumb ${v.id === current ? "on" : ""}`} disabled={v.id === current || busy !== null} title={v.id === current ? "Current" : "Use this version"}
            onClick={() => void run("use", async () => { await coloringApi.useCoverVariant(projectId, v.id); await reload(); })}>
            <img src={coloringApi.imageUrl(projectId, v.id)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
    );
  };

  const sideCard = (side: "front" | "back") => {
    const jobs = side === "front" ? front : back;
    const variantId = side === "front" ? c.frontVariantId : c.backVariantId;
    const file = side === "front" ? frontFile : backFile;
    return (
      <div className="cover-side">
        <div className="cover-side-art">
          <CoverSheet side={side} variantId={variantId} />
          {jobs.open && (
            <div className="page-preview-busy">
              <Icon name="clock" size={20} className="spin" />
              <span>{jobs.open.status === "queued" ? "Waiting in the queue…" : "Generating…"}</span>
            </div>
          )}
        </div>
        <div className="row gap-2" style={{ marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn btn-sm btn-primary" disabled={busy !== null || !!jobs.open || noProvider} onClick={() => void generate(side)}>
            <Icon name={jobs.open ? "clock" : "sparkles"} size={14} className={jobs.open ? "spin" : ""} /> {variantId ? "Regenerate" : "Generate Cover"}{side === "back" ? " (back)" : ""}
          </button>
          <button className="btn btn-sm" disabled={busy !== null} onClick={() => file.current?.click()}>Upload art</button>
          {variantId && (
            <button className="btn btn-sm btn-ghost" disabled={busy !== null} onClick={() => void run("clear", async () => { await coloringApi.clearCover(projectId, side); await reload(); })}>Remove art</button>
          )}
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => { void upload(side, e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {jobs.error && <div className="gen-note err"><Icon name="alert" size={14} /><span>{jobs.error.error}</span></div>}
        {sideStrip(side)}
      </div>
    );
  };

  return (
    <div className="coloring-scroll">
      <div className="cover-layout">
        <div className="cover-fields">
          <SectionCard icon="book" title="Cover text">
            <AutoField label="Title" value={project.title} onSave={(v) => v.trim() ? saveProject({ title: v.trim() }) : undefined} />
            <AutoField label="Subtitle" value={project.subtitle ?? ""} onSave={(v) => saveProject({ subtitle: v.trim() || null })} />
            <AutoField label="Author / creator" value={c.author} onSave={(v) => saveBook({ cover: { author: v } })} placeholder={project.author ?? ""} />
            <AutoField label="Back cover text" rows={5} value={c.backText} onSave={(v) => saveBook({ cover: { backText: v } })} placeholder="A short blurb for the back of the book." hint="Typeset by ClovaScribe, so it's always sharp and correctly spelled." />
          </SectionCard>

          <SectionCard icon="image" title="Cover artwork">
            <AutoField label="Cover style" rows={2} value={c.style} onSave={(v) => saveBook({ cover: { style: v } })} placeholder="Bright, friendly full-color illustration" hint="The cover can be full color even though the pages are black and white." />
            <AutoField label="Front cover description" rows={3} value={c.description} onSave={(v) => saveBook({ cover: { description: v } })} placeholder="Boo the ghost holding a teapot in a cozy garden" />
            <AutoField label="Back cover description" rows={2} value={c.backDescription} onSave={(v) => saveBook({ cover: { backDescription: v } })} placeholder="A calm, simple illustration with space for text" />
            <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
              <Icon name="info" size={12} /> The AI is told not to draw any lettering, so the title and text above are added separately.
            </p>
          </SectionCard>

          <SectionCard icon="settings" title="Typography colors">
            <div className="color-grid">
              {COLOR_FIELDS.map((f) => (
                <label key={f.key} className="color-field">
                  <input type="color" value={c[f.key]} onChange={(e) => void saveBook({ cover: { [f.key]: e.target.value } })} />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <label className="pdf-toggle" style={{ marginTop: 10 }}>
              <input type="checkbox" checked={c.titlePlate} onChange={(e) => void saveBook({ cover: { titlePlate: e.target.checked } })} />
              <span>Soft plate behind text (keeps it readable over artwork)</span>
            </label>
          </SectionCard>
        </div>

        <div className="cover-previews">
          <div>
            <div className="label">Front cover</div>
            {sideCard("front")}
          </div>
          <div>
            <div className="label">Back cover</div>
            {sideCard("back")}
          </div>
          {noProvider && (
            <div className="gen-note warn"><Icon name="alert" size={14} /><span>No image provider is connected, so covers can't be generated. You can upload your own artwork.</span></div>
          )}
        </div>
      </div>
    </div>
  );
}
